import { CommonModule } from "@angular/common";
import { Component, inject, Input, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { NzSelectModule } from "ng-zorro-antd/select";
import { interval, of } from "rxjs";
import { catchError, last, map, switchMap, takeWhile } from "rxjs/operators";
import { RestService } from "../../../core/services/rest.service";
import { ToasterService } from "../../../core/services/toaster-service";
import { MaterialModule } from "../../../core/shared/material.module";
import { DropdownWithSectionsComponent } from "../../common/dropdown-with-sections/dropdown-with-sections.component";
import { LoadingComponent } from "../../common/loading/loading.component";
import { MessageEnum } from "../../../core/utils/message.enum";

interface FileGroup {
  label: string;
  options: { value: string; label: string }[];
}

@Component({
  selector: "app-set-app-context-modal",
  standalone: true,
  imports: [CommonModule,FormsModule,
      MaterialModule,
      DropdownWithSectionsComponent,
      NzSelectModule,
      MatProgressBarModule,
      LoadingComponent, ],
  templateUrl: "./set-app-context-modal.component.html",
  styleUrl: "./set-app-context-modal.component.scss",
})
export class SetAppContextModalComponent implements OnInit {
  activeModal = inject(NgbActiveModal);
  private restService = inject(RestService);
  private toaster = inject(ToasterService);

  isActive:boolean = false;
  isProcessing: boolean = false;
  isLoadingExistingContext: boolean = false;
  progressValue: number = 0;
  progressMessage: string = '';
  isTCGEnabled: boolean = false;
  warningMessage: string = '';
  hasExistingContext: boolean = false;
  existingFileConnection: string = '';

  @Input()
  projectId: string = '';

  @Input()
  fileConnections: any[] = [];

  @Input()
  assets: any[] = [];

  fileConnection: any;
  groupedFileConnections: FileGroup[] = [];

  ngOnInit(): void {
    this.groupFilesByType();
    this.checkTCGAsset();
    this.loadExistingFileConnection();
  }

  /**
   * Load existing file connection if already configured
   */
  loadExistingFileConnection(): void {
    this.isLoadingExistingContext = true;
    this.restService.handleAsyncApplicationContext(this.projectId).subscribe({
      next: (response: any) => {
        if (response?.data?.originalFileName) {
          this.hasExistingContext = true;
          // Find the file connection that matches the originalFileName
          const matchingConnection = this.fileConnections.find(
            conn => conn.meta?.fileName === response.data.originalFileName
          );
          
          if (matchingConnection) {
            this.fileConnection = matchingConnection.id;
            this.existingFileConnection = matchingConnection.id; // Store the existing connection
          }
        }
        this.isLoadingExistingContext = false;
      },
      error: (error: any) => {
        // Silently handle - no existing context is not an error
        console.log('No existing application context found or error loading:', error);
        this.hasExistingContext = false;
        this.isLoadingExistingContext = false;
      }
    });
  }

  /**
   * Check if selected file is same as existing file connection
   */
  get isSameFile(): boolean {
    return this.hasExistingContext && this.fileConnection === this.existingFileConnection;
  }

  /**
   * Check if Test Case Generator asset is enabled
   */
  checkTCGAsset(): void {
    const tcgAsset = this.assets.find(
      (asset: any) => asset.name === 'testCaseGenerator' && asset.alreadyMapped === true
    );
    
    this.isTCGEnabled = !!tcgAsset;
    
    if (!this.isTCGEnabled) {
      this.warningMessage = 'Please enable the Test Case Generator asset to set up application context.';
    }
  }

  /**
   * Group file connections by file type
   */
  groupFilesByType(): void {
    const groups: { [key: string]: any[] } = {};

    this.fileConnections.forEach(connection => {
      const fileName = connection.meta?.fileName || connection.connectionName;
      const fileExtension = this.getFileExtension(fileName);
      const groupName = this.getFileTypeLabel(fileExtension);

      if (!groups[groupName]) {
        groups[groupName] = [];
      }

      groups[groupName].push({
        value: connection.id,
        label: connection.connectionName
      });
    });

    // Convert to array format for nz-option-group and sort with Word Documents first
    this.groupedFileConnections = Object.keys(groups)
      .map(key => ({
        label: key,
        options: groups[key]
      }))
      .sort((a, b) => {
        if (a.label === 'Word Documents') return -1;
        if (b.label === 'Word Documents') return 1;
        return a.label.localeCompare(b.label);
      });
  }

  /**
   * Extract file extension from filename
   */
  private getFileExtension(fileName: string): string {
    const parts = fileName.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : 'other';
  }

  /**
   * Get user-friendly label for file type
   */
  private getFileTypeLabel(extension: string): string {
    const typeMap: { [key: string]: string } = {
      'xlsx': 'Excel Files',
      'xls': 'Excel Files',
      'csv': 'CSV Files',
      'json': 'JSON Files',
      'xml': 'XML Files',
      'txt': 'Text Files',
      'pdf': 'PDF Files',
      'doc': 'Word Documents',
      'docx': 'Word Documents',
      'sql': 'SQL Files'
    };

    return typeMap[extension] || 'Other Files';
  }

  save(){
    if (!this.fileConnection) {
      this.toaster.pop(
        {
          type: "error",
          text: MessageEnum.SELECT_FILE_CONNECTION,
        },
        { backgroundColor: "transparent" }
      );
      return;
    }

    this.isProcessing = true;
    this.progressValue = 5;
    this.progressMessage = 'Initiating application context setup...';

    // Check if user selected a different file than the existing one
    const isDifferentFile = this.hasExistingContext && this.fileConnection !== this.existingFileConnection;

    // If there's an existing context and user selected a different file, delete it first
    if (isDifferentFile) {
      this.progressMessage = 'Deleting existing application context...';
      this.restService.deleteApplicationContext(this.projectId)
        .pipe(
          catchError((error) => {
            console.log('Error deleting existing context:', error);
            // Continue even if delete fails (context might not exist anymore)
            return of(null);
          })
        )
        .subscribe({
          next: () => {
            this.progressValue = 10;
            this.progressMessage = 'Existing context deleted. Setting up new context...';
            this.appContextSetup(this.fileConnection);
          },
          error: (error: any) => {
            console.error('Unexpected error during deletion:', error);
            // Continue with setup anyway
            // this.progressValue = 10;
            // this.appContextSetup(this.fileConnection);
          }
        });
    } else {
      this.progressValue = 10;
      this.appContextSetup(this.fileConnection);
    }
  }

  /**
   * Set up application context with progress tracking
   */
  appContextSetup(fileConnectionId: string) {
    this.restService
      .processApplicationContext(this.projectId, fileConnectionId)
      .pipe(
        switchMap(() => {
          this.progressValue = 30;
          this.progressMessage = 'Processing application context...';
          
          return interval(20000).pipe(
            switchMap(() =>
              this.restService
                .handleAsyncApplicationContext(this.projectId)
                .pipe(
                  map((statusResponse: any) => {
                    if (statusResponse?.data?.status === "COMPLETED") {
                      this.progressValue = 100;
                      this.progressMessage = 'Application context setup completed!';
                      
                      setTimeout(() => {
                        this.isProcessing = false;
                        this.toaster.pop(
                          {
                            type: "success",
                            text: statusResponse?.data?.message || MessageEnum.APP_CONTEXT_SETUP_SUCCESS,
                          },
                          { backgroundColor: "transparent" }
                        );
                        this.activeModal.close({ success: true });
                      }, 1000);
                      
                      return { success: true };
                    } else if (statusResponse?.data?.status === "FAILED") {
                      this.isProcessing = false;
                      this.progressMessage = 'Application context setup failed';
                      this.toaster.pop(
                        {
                          type: "error",
                          text: statusResponse?.data?.message || MessageEnum.APP_CONTEXT_SETUP_FAILURE,
                        },
                        { backgroundColor: "transparent" }
                      );
                      throw new Error("Application context generation failed.");
                    } else {
                      // Still processing - update progress
                      this.progressValue = Math.min(this.progressValue + 10, 90);
                      this.progressMessage = statusResponse?.data?.message || 'Processing application context...';
                      return { success: false };
                    }
                  })
                )
            ),
            takeWhile((result) => !result.success, true),
            last()
          );
        })
      )
      .subscribe({
        next: () => {
          console.log("Application context setup completed");
        },
        error: (error: any) => {
          this.isProcessing = false;
          this.progressMessage = 'Error occurred during setup';
          
          // Check if it's a 400 error
          if (error?.status === 400 || error?.error?.status === 400) {
            this.toaster.pop(
              {
                type: "error",
                text: error?.error?.message || error?.message || MessageEnum.BAD_REQUEST,
              },
              { backgroundColor: "transparent" }
            );
          } else {
            this.toaster.pop(
              {
                type: "error",
                text: error?.error?.message || error?.message || MessageEnum.APP_CONTEXT_SETUP_FAILURE,
              },
              { backgroundColor: "transparent" }
            );
          }
          
          console.error("Application context setup error:", error);
        },
      });
  }
}
